import type { ReactNode } from "react";
import type { SettingsSending } from "../../shared/types";

export type HostKey = NonNullable<SettingsSending["dns_host"]> | "other";

export interface Host {
  label: string;
  steps: (root: string) => ReactNode[];
  tip: string;
  /** What the host's own form calls the Type, Name and Value boxes. */
  fields: [string, string, string];
}

/** Where to paste the records, step by step, at each DNS host setup knows, and anywhere else. */
export const HOSTS: Record<HostKey, Host> = {
  godaddy: {
    label: "GoDaddy",
    steps: (root) => [
      <>Sign in to <b>GoDaddy</b> and open <b>My Products</b>.</>,
      <>Next to <b>{root}</b>, choose <b>DNS</b>.</>,
      <>Press <b>Add New Record</b>. Pick the <b>Type</b> from the row on the left.</>,
      <>Copy the <b>Name</b> into <b>Name</b> and the <b>Value</b> into <b>Value</b>. For an MX row, put 10 in <b>Priority</b>.</>,
      <>Press <b>Save</b>, then do the next row. Leave <b>TTL</b> as it is.</>,
    ],
    tip: "GoDaddy adds {root} to the end of the Name by itself, so paste only what you see here.",
    fields: ["Type", "Name", "Value"],
  },
  namecheap: {
    label: "Namecheap",
    steps: (root) => [
      <>Sign in to <b>Namecheap</b> and open <b>Domain List</b>.</>,
      <>Next to <b>{root}</b>, press <b>Manage</b>, then open <b>Advanced DNS</b>.</>,
      <>Under <b>Host records</b>, press <b>Add new record</b> and pick the <b>Type</b>.</>,
      <>Copy the <b>Name</b> into <b>Host</b> and the <b>Value</b> into <b>Value</b>.</>,
      <>For an MX row, choose <b>Custom MX</b> under <b>Mail settings</b> first. Press the green tick to save each row.</>,
    ],
    tip: "Namecheap calls the Name a Host. Paste only what you see here.",
    fields: ["Type", "Host", "Value"],
  },
  cloudflare: {
    label: "Cloudflare",
    steps: (root) => [
      <>Sign in to <b>Cloudflare</b> and pick <b>{root}</b>.</>,
      <>Open <b>DNS</b>, then <b>Records</b>.</>,
      <>Press <b>Add record</b> and pick the <b>Type</b>.</>,
      <>Copy the <b>Name</b> into <b>Name</b> and the <b>Value</b> into <b>Content</b>. For an MX row, put 10 in <b>Priority</b>.</>,
      <>Press <b>Save</b>, then do the next row.</>,
    ],
    tip: "",
    fields: ["Type", "Name", "Content"],
  },
  squarespace: {
    label: "Squarespace",
    steps: (root) => [
      <>Sign in to <b>Squarespace</b> and open <b>Domains</b>.</>,
      <>Pick <b>{root}</b>, then <b>DNS</b>.</>,
      <>Under <b>Custom records</b>, press <b>Add record</b>.</>,
      <>Pick the <b>Type</b>, copy the <b>Name</b> into <b>Host</b> and the <b>Value</b> into <b>Data</b>.</>,
      <>Press <b>Save</b>, then do the next row.</>,
    ],
    tip: "Squarespace calls the Name a Host and the Value Data.",
    fields: ["Type", "Host", "Data"],
  },
  other: {
    label: "Other",
    steps: (root) => [
      <>Sign in where you bought <b>{root}</b>, or where your website lives.</>,
      <>Look for <b>DNS</b>, <b>Zone editor</b> or <b>Advanced DNS</b>.</>,
      <>Add a new record for each row: pick the <b>Type</b>, then copy the <b>Name</b> and the <b>Value</b>.</>,
      <>Save each one.</>,
    ],
    tip: "",
    fields: ["Type", "Name", "Value"],
  },
};

export const HOST_ORDER: HostKey[] = ["godaddy", "namecheap", "cloudflare", "squarespace", "other"];
